import React from 'react';
import { 
  Typography, Card, CardContent, Grid, Box, Paper, Avatar, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress
} from '@mui/material';
import { 
  TrendingUp, TrendingDown, People, Hotel, AttachMoney, EventAvailable,
  CheckCircle, AccessTime, Cancel, Star
} from '@mui/icons-material';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Mock data for charts
const revenueData = [
  { month: 'Jan', revenue: 45000, bookings: 120 },
  { month: 'Feb', revenue: 52000, bookings: 145 },
  { month: 'Mar', revenue: 48000, bookings: 132 },
  { month: 'Apr', revenue: 61000, bookings: 168 },
  { month: 'May', revenue: 55000, bookings: 152 },
  { month: 'Jun', revenue: 67000, bookings: 185 },
];

const roomTypeData = [
  { name: 'Deluxe', value: 35, color: '#1976d2' },
  { name: 'Suite', value: 25, color: '#dc004e' },
  { name: 'Standard', value: 40, color: '#9c27b0' },
];

const occupancyData = [
  { day: 'Mon', rate: 85 },
  { day: 'Tue', rate: 78 },
  { day: 'Wed', rate: 90 },
  { day: 'Thu', rate: 88 },
  { day: 'Fri', rate: 95 },
  { day: 'Sat', rate: 98 },
  { day: 'Sun', rate: 92 },
];

const recentBookings = [
  { id: 1, guest: 'John Smith', room: 'Deluxe 204', checkIn: '2025-11-16', status: 'Confirmed', amount: '$450' },
  { id: 2, guest: 'Emma Wilson', room: 'Suite 305', checkIn: '2025-11-17', status: 'Pending', amount: '$680' },
  { id: 3, guest: 'Michael Brown', room: 'Standard 102', checkIn: '2025-11-16', status: 'Checked In', amount: '$320' },
  { id: 4, guest: 'Sarah Davis', room: 'Deluxe 208', checkIn: '2025-11-18', status: 'Confirmed', amount: '$450' },
];

const StatCard = ({ title, value, change, icon: Icon, color }) => (
  <Card sx={{ 
    height: '100%',
    background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
    border: `1px solid ${color}30`,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: { xs: 'none', sm: 'translateY(-4px)' },
      boxShadow: { xs: 'none', sm: `0 8px 24px ${color}30` },
    }
  }}>
    <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 }, '&:last-child': { pb: { xs: 1, sm: 2, md: 3 } } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography color="text.secondary" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' }, lineHeight: 1.2, mb: { xs: 0.5, sm: 1 } }}>
            {value}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.3}>
            {change > 0 ? <TrendingUp sx={{ fontSize: { xs: 12, sm: 16 } }} color="success" /> : <TrendingDown sx={{ fontSize: { xs: 12, sm: 16 } }} color="error" />}
            <Typography variant="body2" color={change > 0 ? 'success.main' : 'error.main'} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.875rem' } }}>
              {change > 0 ? '+' : ''}{change}%
            </Typography>
          </Box>
        </Box>
        <Avatar sx={{ bgcolor: color, width: { xs: 36, sm: 48, md: 56 }, height: { xs: 36, sm: 48, md: 56 }, flexShrink: 0 }}>
          <Icon sx={{ fontSize: { xs: 20, sm: 28, md: 36 } }} />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  return (
    <Box sx={{ pb: 8 }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Dashboard Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's what's happening with your hotel today.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" size="small">Export</Button>
          <Button variant="contained" size="small">Add Booking</Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Revenue" value="$67,450" change={12.5} icon={AttachMoney} color="#1976d2" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Bookings" value="185" change={8.2} icon={EventAvailable} color="#2e7d32" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Occupancy Rate" value="92%" change={5.4} icon={Hotel} color="#ed6c02" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Active Guests" value="156" change={-2.1} icon={People} color="#9c27b0" />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: { xs: '300px', sm: '400px' } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Revenue & Bookings Trend
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" style={{ fontSize: '12px' }} />
                <YAxis style={{ fontSize: '12px' }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Area type="monotone" dataKey="revenue" stroke="#1976d2" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue ($)" />
                <Line type="monotone" dataKey="bookings" stroke="#dc004e" strokeWidth={2} name="Bookings" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: { xs: '300px', sm: '400px' } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Room Distribution
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={roomTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  style={{ fontSize: '11px' }}
                >
                  {roomTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Occupancy Rate */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Weekly Occupancy Rate
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={occupancyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" style={{ fontSize: '12px' }} />
                <YAxis style={{ fontSize: '12px' }} />
                <Tooltip />
                <Bar dataKey="rate" fill="#2e7d32" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Quick Stats
            </Typography>
            <Box mt={2}>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Check-ins Today</Typography>
                  <Typography variant="body2" fontWeight="bold">24/30</Typography>
                </Box>
                <LinearProgress variant="determinate" value={80} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Check-outs Today</Typography>
                  <Typography variant="body2" fontWeight="bold">18/22</Typography>
                </Box>
                <LinearProgress variant="determinate" value={82} color="secondary" sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Room Cleaning</Typography>
                  <Typography variant="body2" fontWeight="bold">35/42</Typography>
                </Box>
                <LinearProgress variant="determinate" value={83} color="success" sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Maintenance Pending</Typography>
                  <Typography variant="body2" fontWeight="bold">3/5</Typography>
                </Box>
                <LinearProgress variant="determinate" value={60} color="warning" sx={{ height: 8, borderRadius: 4 }} />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Bookings */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Recent Bookings
          </Typography>
          <Button size="small">View All</Button>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }} size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Guest Name</strong></TableCell>
                <TableCell><strong>Room</strong></TableCell>
                <TableCell><strong>Check-in</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentBookings.map((booking) => (
                <TableRow key={booking.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>
                        {booking.guest[0]}
                      </Avatar>
                      {booking.guest}
                    </Box>
                  </TableCell>
                  <TableCell>{booking.room}</TableCell>
                  <TableCell>{booking.checkIn}</TableCell>
                  <TableCell>
                    <Chip 
                      label={booking.status} 
                      size="small"
                      color={
                        booking.status === 'Confirmed' ? 'success' : 
                        booking.status === 'Checked In' ? 'primary' : 'warning'
                      }
                      icon={
                        booking.status === 'Confirmed' ? <CheckCircle /> :
                        booking.status === 'Checked In' ? <Star /> : <AccessTime />
                      }
                    />
                  </TableCell>
                  <TableCell><strong>{booking.amount}</strong></TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined">View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
